import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

export interface User {
  id: string;
  email: string;
  name: string;
  sports: string[];
  createdAt: string;
  subscription: 'free' | 'premium' | 'pro';
  role?: 'user' | 'admin';
  clubs: {
    [sportId: string]: {
      clubId: string;
      clubName: string;
      ranking: number;
      joinedAt: string;
      isActive: boolean;
    };
  };
}

interface AuthContextType {
  user: User | null;
  currentUser: User | null; // alias for ProtectedRoute compatibility
  loading: boolean;
  isAdmin: boolean;
  signUp: (email: string, password: string, name: string) => Promise<{ success: boolean; error?: string }>;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signInWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  signOut: () => void;
  updateUser: (updates: Partial<User>) => void;
  addSport: (sport: string) => void;
  removeSport: (sport: string) => void;
  joinClub: (sportId: string, clubId: string, clubName: string) => Promise<{ success: boolean; error?: string }>;
  leaveClub: (sportId: string) => Promise<{ success: boolean; error?: string }>;
  updateRanking: (sportId: string, newRanking: number) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const ADMIN_EMAILS = ['admin@mysportscoach.online', 'sumush@gmail.com'];

function firebaseUserToAppUser(fbUser: FirebaseUser, profile?: Partial<User>): User {
  const email = (fbUser.email || '').toLowerCase();
  const isAdminEmail = ADMIN_EMAILS.some((e) => e.toLowerCase() === email);
  const subscription = isAdminEmail ? 'premium' : (profile?.subscription ?? 'free');
  return {
    id: fbUser.uid,
    email: fbUser.email || '',
    name: profile?.name ?? fbUser.displayName ?? fbUser.email ?? 'User',
    sports: profile?.sports ?? ['pickleball'],
    createdAt: profile?.createdAt ?? new Date().toISOString(),
    subscription,
    role: profile?.role ?? (isAdminEmail ? 'admin' : 'user'),
    clubs: profile?.clubs ?? {},
  };
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user && (ADMIN_EMAILS.some((e) => e.toLowerCase() === (user.email || '').toLowerCase()) || user.role === 'admin');

  // Load Firestore profile and merge with Auth user
  const loadUserProfile = async (fbUser: FirebaseUser): Promise<User> => {
    const userRef = doc(db, 'users', fbUser.uid);
    const snap = await getDoc(userRef);
    const data = snap.data();

    const email = (fbUser.email || '').toLowerCase();
    const isAdminEmail = ADMIN_EMAILS.some((e) => e.toLowerCase() === email);
    const subscription = isAdminEmail ? 'premium' : (data?.subscription ?? 'free');

    const appUser: User = firebaseUserToAppUser(fbUser, {
      name: data?.displayName ?? data?.name ?? fbUser.displayName ?? fbUser.email ?? 'User',
      sports: data?.sports ?? ['pickleball'],
      createdAt: data?.createdAt?.toDate?.()?.toISOString?.() ?? data?.createdAt ?? new Date().toISOString(),
      subscription,
      role: data?.role ?? (isAdminEmail ? 'admin' : 'user'),
      clubs: data?.clubs ?? {},
    });

    return appUser;
  };

  // Create or update Firestore doc for new users
  const ensureUserDoc = async (fbUser: FirebaseUser, displayName?: string) => {
    const userRef = doc(db, 'users', fbUser.uid);
    const snap = await getDoc(userRef);

    if (!snap.exists()) {
      await setDoc(userRef, {
        email: fbUser.email,
        displayName: displayName ?? fbUser.displayName ?? fbUser.email ?? 'User',
        name: displayName ?? fbUser.displayName ?? fbUser.email ?? 'User',
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        sports: ['pickleball'],
        subscription: 'free',
        clubs: {},
      });
    } else {
      await updateDoc(userRef, { lastLogin: serverTimestamp() });
    }
  };

  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          await ensureUserDoc(result.user);
          const appUser = await loadUserProfile(result.user);
          setUser(appUser);
        }
      } catch (err) {
        console.error('Redirect result error:', err);
      }
    };
    handleRedirectResult();
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        try {
          const appUser = await loadUserProfile(fbUser);
          setUser(appUser);
        } catch (err) {
          console.error('Load profile error:', err);
          setUser(firebaseUserToAppUser(fbUser));
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const persistUserToFirestore = async (u: User) => {
    if (!auth.currentUser) return;
    const userRef = doc(db, 'users', u.id);
    await updateDoc(userRef, {
      displayName: u.name,
      name: u.name,
      sports: u.sports,
      subscription: u.subscription,
      role: u.role,
      clubs: u.clubs,
    });
  };

  const signUp = async (email: string, password: string, name: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: name });
      await ensureUserDoc(cred.user, name);
      const appUser = await loadUserProfile(cred.user);
      setUser(appUser);
      return { success: true };
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      const msg =
        e?.code === 'auth/email-already-in-use'
          ? 'This email is already registered. Try signing in instead.'
          : e?.code === 'auth/weak-password'
            ? 'Password should be at least 6 characters.'
            : e?.message ?? 'Failed to create account';
      return { success: false, error: msg };
    }
  };

  const signIn = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await ensureUserDoc(cred.user);
      const appUser = await loadUserProfile(cred.user);
      setUser(appUser);
      return { success: true };
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      const msg =
        e?.code === 'auth/invalid-credential' || e?.code === 'auth/user-not-found' || e?.code === 'auth/wrong-password'
          ? 'Invalid email or password.'
          : e?.message ?? 'Failed to sign in';
      return { success: false, error: msg };
    }
  };

  const signInWithGoogle = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');

      let result;
      try {
        result = await signInWithPopup(auth, provider);
      } catch (popupErr: unknown) {
        const pe = popupErr as { code?: string; message?: string };
        if (
          pe?.code === 'auth/popup-blocked' ||
          pe?.code === 'auth/popup-closed-by-user' ||
          pe?.message?.includes('cross-origin') ||
          pe?.message?.includes('CORS')
        ) {
          await signInWithRedirect(auth, provider);
          return { success: true }; // Page will reload
        }
        throw popupErr;
      }

      if (result?.user) {
        await ensureUserDoc(result.user);
        const appUser = await loadUserProfile(result.user);
        setUser(appUser);
        return { success: true };
      }
      return { success: false, error: 'Sign-in was cancelled.' };
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      const msg =
        e?.code === 'auth/popup-closed-by-user'
          ? 'Sign-in was cancelled.'
          : e?.message ?? 'Failed to sign in with Google';
      return { success: false, error: msg };
    }
  };

  const signOut = () => {
    firebaseSignOut(auth);
    setUser(null);
  };

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      const updated = { ...user, ...updates };
      setUser(updated);
      persistUserToFirestore(updated).catch(console.error);
    }
  };

  const addSport = (sport: string) => {
    if (user && !user.sports.includes(sport)) {
      updateUser({ sports: [...user.sports, sport] });
    }
  };

  const removeSport = (sport: string) => {
    if (user) {
      updateUser({ sports: user.sports.filter((s) => s !== sport) });
    }
  };

  const joinClub = async (sportId: string, clubId: string, clubName: string): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: 'User not logged in' };
    try {
      const updatedClubs = {
        ...user.clubs,
        [sportId]: {
          clubId,
          clubName,
          ranking: 1,
          joinedAt: new Date().toISOString(),
          isActive: true,
        },
      };
      updateUser({ clubs: updatedClubs });
      return { success: true };
    } catch (err) {
      console.error('Error joining club:', err);
      return { success: false, error: 'Failed to join club' };
    }
  };

  const leaveClub = async (sportId: string): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: 'User not logged in' };
    try {
      const updatedClubs = { ...user.clubs };
      delete updatedClubs[sportId];
      updateUser({ clubs: updatedClubs });
      return { success: true };
    } catch (err) {
      console.error('Error leaving club:', err);
      return { success: false, error: 'Failed to leave club' };
    }
  };

  const updateRanking = async (sportId: string, newRanking: number): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: 'User not logged in' };
    if (!user.clubs[sportId]) return { success: false, error: 'User is not a member of any club for this sport' };
    try {
      const updatedClubs = {
        ...user.clubs,
        [sportId]: { ...user.clubs[sportId], ranking: newRanking },
      };
      updateUser({ clubs: updatedClubs });
      return { success: true };
    } catch (err) {
      console.error('Error updating ranking:', err);
      return { success: false, error: 'Failed to update ranking' };
    }
  };

  const value: AuthContextType = {
    user,
    currentUser: user,
    loading,
    isAdmin,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    updateUser,
    addSport,
    removeSport,
    joinClub,
    leaveClub,
    updateRanking,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
